import { types as sdkTypes } from './sdkLoader';
import { createResourceLocatorString } from './routes';
import { PRODUCT_LISTING_TYPE, SERVICE_LISTING_TYPE } from './types';

const { UUID } = sdkTypes;
const draftId = '00000000-0000-0000-0000-000000000000';

const canUseLocalStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readOnboardingListingIds = () => {
  if (!canUseLocalStorage()) {
    return [];
  }

  try {
    const stored = window.localStorage.getItem('onboardingListingIds') || '[]';
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error accessing onboarding listings:', error);
    try {
      window.localStorage.removeItem('onboardingListingIds');
    } catch (removeError) {
      console.error('Error clearing onboarding listings:', removeError);
    }
    return [];
  }
};

const writeOnboardingListingIds = listings => {
  if (!canUseLocalStorage()) {
    return;
  }

  window.localStorage.setItem('onboardingListingIds', JSON.stringify(listings));
};

const clearOnboardingListingIds = () => {
  if (!canUseLocalStorage()) {
    return;
  }

  window.localStorage.removeItem('onboardingListingIds');
};

/**
 * Utility function to safely get onboarding listings from localStorage
 * Filters out listings that don't belong to the current user
 * @param {string} currentUserId - Current user's ID
 * @param {function} getOwnListing - Function to get user's own listing by ID
 * @returns {Array} Array of valid onboarding listings
 */
export const getSafeOnboardingListings = (currentUserId, getOwnListing) => {
  try {
    const onboardingListingIds = readOnboardingListingIds();
    
    if (!Array.isArray(onboardingListingIds) || onboardingListingIds.length === 0) {
      return [];
    }

    // Filter out listings that don't belong to current user
    const validListings = onboardingListingIds.filter(storedListing => {
      if (!storedListing.id || storedListing.id === draftId) {
        return true; // Keep draft entries
      }
      
      try {
        const listing = getOwnListing(new UUID(storedListing.id));
        return listing && listing.author?.id?.uuid === currentUserId;
      } catch (error) {
        return false; // Remove invalid listings
      }
    });

    // Update localStorage with filtered listings
    if (validListings.length !== onboardingListingIds.length) {
      console.log('🧹 Cleaned up invalid onboarding listings');
      writeOnboardingListingIds(validListings);
    }

    return validListings;
  } catch (error) {
    console.error('Error accessing onboarding listings:', error);
    clearOnboardingListingIds();
    return [];
  }
};

/**
 * Utility function to clear localStorage if listings don't belong to current user
 * @param {string} currentUserId - Current user's ID
 * @param {function} getOwnListing - Function to get user's own listing by ID
 */
export const clearInvalidOnboardingListings = async (currentUserId, getOwnListing) => {
  try {
    const onboardingListingIds = readOnboardingListingIds();
    
    if (!Array.isArray(onboardingListingIds) || onboardingListingIds.length === 0) {
      return; // No listings to check
    }

    // Check if any of the stored listings belong to the current user
    let hasValidListings = false;
    
    for (const storedListing of onboardingListingIds) {
      if (storedListing.id && storedListing.id !== draftId) {
        try {
          const listing = getOwnListing(new UUID(storedListing.id));
          if (listing && listing.author?.id?.uuid === currentUserId) {
            hasValidListings = true;
            break;
          }
        } catch (error) {
          // Invalid UUID or listing doesn't exist, continue checking
          continue;
        }
      }
    }

    // If no valid listings found for current user, clear localStorage
    if (!hasValidListings) {
      console.log('🧹 Clearing onboarding listings for new merchant');
      clearOnboardingListingIds();
    }
  } catch (error) {
    console.error('Error checking onboarding listings:', error);
    // If there's any error, clear localStorage to be safe
    clearOnboardingListingIds();
  }
};

/**
 * A target is a "brand new" service/product exactly when it has no id yet,
 * or its id is still the shared draft placeholder — i.e. the merchant has
 * never started (or never finished) a service/product listing.
 */
export const isNewProductOrServiceOnboardingTarget = target =>
  (target?.formType === SERVICE_LISTING_TYPE || target?.formType === PRODUCT_LISTING_TYPE) &&
  (!target?.id || target.id === draftId);

// Mirrors MerchantOnboardingPage's own AGREEMENT_FORM_TYPE constant — kept as
// a literal here (rather than imported) since that page itself imports from
// this module.
const AGREEMENT_FORM_TYPE = 'agreement';

/**
 * Whether the merchant has signed the onboarding agreement. Prefers the
 * public boolean flag (set on signing); falls back to the private record for
 * robustness. Mirrors the same check MerchantOnboardingPage's hard gate uses.
 */
export const isMerchantAgreementSigned = currentUser =>
  !!currentUser?.attributes?.profile?.publicData?.isAgreementSigned ||
  !!currentUser?.attributes?.profile?.privateData?.merchantAgreement?.signed;

/**
 * Resolves where a merchant should land for a given onboarding target
 * ({id, formType}). The agreement must be signed before anything else, so an
 * unsigned merchant is always routed to the agreement step regardless of
 * target. Otherwise, a brand-new service/product draft is routed through the
 * category picker first (NewListingCategoryPage, step 1 of service
 * creation — the category drives the transaction model, which is
 * admin-owned) instead of landing directly on AddItemForm with unlocked
 * category dropdowns. Every other target (an existing draft/listing, or the
 * deal / business-profile / agreement steps) goes straight to
 * MerchantOnboardingPage.
 *
 * Centralizes a decision that used to be reimplemented ad hoc at every call
 * site that links into onboarding (Topbar's "Complete Your Profile" modal,
 * MerchantDetailsPage's post-signup redirect, ViewListingsPage's "create
 * service" CTA, and MerchantOnboardingPage's own sidebar/tab navigation) —
 * three of those had no category-picker hop at all before this existed. The
 * category-picker route (NewListingPage) doesn't mount MerchantOnboardingPage
 * at all, so its own agreement gate never got a chance to run for those
 * three call sites — a first-time merchant landed straight on the category
 * picker, skipping the agreement step entirely.
 *
 * @param {{id: string, formType: string}} target
 * @param {Array} routeConfiguration
 * @param {Object} currentUser
 */
export const getOnboardingNavigationPath = (target, routeConfiguration, currentUser) => {
  if (!isMerchantAgreementSigned(currentUser)) {
    return createResourceLocatorString(
      'MerchantOnboardingPage',
      routeConfiguration,
      { id: draftId, formType: AGREEMENT_FORM_TYPE },
      {}
    );
  }

  return isNewProductOrServiceOnboardingTarget(target)
    ? createResourceLocatorString(
        'NewListingPage',
        routeConfiguration,
        {},
        { listingType: target.formType, onboarding: '1' }
      )
    : createResourceLocatorString('MerchantOnboardingPage', routeConfiguration, target, {});
};

/**
 * Has the merchant finished the deal step — either by publishing a deal, or by
 * legitimately having nothing that could back one?
 *
 * A negotiated service has no fixed price to discount from, so it's excluded
 * from the deal picker (see AddDealForm's allItems). A merchant whose only
 * services are negotiated therefore reaches the deal step with an empty picker
 * and no way to satisfy its required `deal_items` field — they skip it instead,
 * which records `dealStepSkipped`.
 *
 * Note this is sticky: a merchant who skips and later adds a fixed service
 * won't be nudged back to create a deal.
 */
export const isDealStepSatisfied = profilePublicData =>
  !!(profilePublicData?.isDealListingPublished || profilePublicData?.dealStepSkipped);

/**
 * Whether a merchant has finished onboarding — service/product published, deal
 * step satisfied, business profile published.
 *
 * Centralizes a condition that was reimplemented at every call site that gates
 * on it (Topbar's "complete your profile" modal, ViewListingsPage, DashboardPage).
 * Duplicating it is what let the deal-step skip leak: fixing three of the call
 * sites would leave the fourth nagging a merchant who has nothing to fix.
 *
 * @param {Object} profilePublicData currentUser.attributes.profile.publicData
 */
export const isMerchantOnboardingComplete = profilePublicData =>
  !!profilePublicData?.isProductServiceListingPublished &&
  isDealStepSatisfied(profilePublicData) &&
  !!profilePublicData?.isBusinessListingPublished;

/**
 * The onboarding steps a merchant can still owe, in the order they must be
 * done. `agreement` always comes first (MerchantOnboardingPage hard-gates every
 * other step on it).
 */
export const ONBOARDING_STEP = {
  AGREEMENT: 'agreement',
  SERVICE: 'service',
  DEAL: 'deal',
  BUSINESS: 'business',
};

/**
 * The first onboarding step this merchant still owes, or null when they're done.
 * Derived purely from the merchant's own state, so it's right for every way a
 * merchant can come into existence — the web wizard, the mobile app, or created
 * from Zoho CRM (where the business/service/deal already exist and only the
 * agreement is left).
 *
 * The agreement is part of "onboarded". isMerchantOnboardingComplete (above)
 * deliberately only covers the three published-listing flags and is kept as is;
 * every "should I nudge / where do I send them" decision should use this
 * instead, so a merchant who has published everything but never signed is still
 * prompted (previously they were treated as fully onboarded and never asked).
 *
 * @param {Object} currentUser
 * @returns {'agreement'|'service'|'deal'|'business'|null}
 */
export const getNextOnboardingStep = currentUser => {
  const publicData = currentUser?.attributes?.profile?.publicData;

  if (!isMerchantAgreementSigned(currentUser)) {
    return ONBOARDING_STEP.AGREEMENT;
  }
  if (!publicData?.isProductServiceListingPublished) {
    return ONBOARDING_STEP.SERVICE;
  }
  if (!isDealStepSatisfied(publicData)) {
    return ONBOARDING_STEP.DEAL;
  }
  if (!publicData?.isBusinessListingPublished) {
    return ONBOARDING_STEP.BUSINESS;
  }
  return null;
};

/**
 * Whether the merchant has nothing left to do: all three listings published (or
 * the deal step legitimately skipped) AND the agreement signed.
 */
export const isMerchantFullyOnboarded = currentUser => getNextOnboardingStep(currentUser) === null;
